import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './createListing.css';
import { useAuth } from '../../AuthContext';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { CUISINE_TYPES, BOOKING_CUTOFF_OPTIONS } from '../../constants/listingOptions';
import { NEIGHBORHOODS } from '../../constants/neighborhoods';
import { useUserProfile } from '../../hooks/useUserProfile';
import {
  TitleField,
  DescriptionField,
  MenuField,
  DietaryField,
  AlcoholField,
  SpecialNotesField,
} from '../../components/MealFormFields';

const CreateListing = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cuisineType: '',
    menu: '',
    dateTime: '',
    maxGuests: '',
    street: '',
    unitNumber: '',
    city: '',
    state: '',
    zipCode: '',
    neighborhood: '',
    dietaryAccommodations: [],
    alcoholOption: 'none',
    bookingCutoff: '',
    specialNotes: ''
  });

  const [errors, setErrors] = useState({});

  const { profile: userProfile } = useUserProfile(currentUser?.uid);

  useEffect(() => {
    if (userProfile?.neighborhood) {
      setFormData(prev => ({ ...prev, neighborhood: userProfile.neighborhood }));
    }
  }, [userProfile]);

  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    // Cuisine type validation
    if (!formData.cuisineType) {
      newErrors.cuisineType = 'Please select a cuisine type';
    }

    // Menu validation (optional, but enforce max length if provided)
    if (formData.menu.length > 500) {
      newErrors.menu = 'Menu must be 500 characters or less';
    }

    // Date/time validation
    if (!formData.dateTime) {
      newErrors.dateTime = 'Date and time are required';
    } else {
      const selectedDate = new Date(formData.dateTime);
      const now = new Date();
      if (selectedDate <= now) {
        newErrors.dateTime = 'Date must be in the future';
      }
    }

    // Max guests validation
    const guests = parseInt(formData.maxGuests, 10);
    if (!formData.maxGuests) {
      newErrors.maxGuests = 'Number of guests is required';
    } else if (isNaN(guests) || guests < 1) {
      newErrors.maxGuests = 'Must be at least 1 guest';
    } else if (guests > 8) {
      newErrors.maxGuests = 'Maximum 8 guests allowed';
    }

    // Street validation
    if (!formData.street.trim()) {
      newErrors.street = 'Street address is required';
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    // State validation
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    // Zip code validation
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'Zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
      newErrors.zipCode = 'Please enter a valid zip code';
    }

    // Neighborhood validation
    if (!formData.neighborhood) {
      newErrors.neighborhood = 'Neighborhood is required';
    }

    // Booking cutoff validation
    if (!formData.bookingCutoff) {
      newErrors.bookingCutoff = 'Booking cutoff time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDietaryChange = (option) => {
    setFormData(prev => {
      const current = prev.dietaryAccommodations;
      if (current.includes(option)) {
        return {
          ...prev,
          dietaryAccommodations: current.filter(item => item !== option)
        };
      } else {
        return {
          ...prev,
          dietaryAccommodations: [...current, option]
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    if (!currentUser) {
      setError('You must be logged in to create a meal');
      return;
    }

    setLoading(true);

    try {
      const createMeal = httpsCallable(functions, 'createMeal');
      const { data: meal } = await createMeal({
        title: formData.title.trim(),
        description: formData.description.trim(),
        cuisineType: formData.cuisineType,
        menu: formData.menu.trim(),
        dateTime: new Date(formData.dateTime).toISOString(),
        maxGuests: parseInt(formData.maxGuests, 10),
        neighborhood: formData.neighborhood,
        street: formData.street.trim(),
        unitNumber: formData.unitNumber.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zipCode: formData.zipCode.trim(),
        dietaryAccommodations: formData.dietaryAccommodations,
        alcoholOption: formData.alcoholOption,
        bookingCutoff: parseInt(formData.bookingCutoff, 10),
        specialNotes: formData.specialNotes.trim(),
      });

      // Redirect to the meal summary page
      navigate(`/meal-summary/${meal.id}`);
    } catch (err) {
      console.error('Error creating meal:', err);
      setError(err.message || 'Failed to create meal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-listing-container">
      <div className="create-listing-card">
        <h1>Create a Meal</h1>
        <p className="create-listing-subtitle">
          Share your culinary experience with guests
        </p>

        {error && <div className="create-listing-error">{error}</div>}

        <form onSubmit={handleSubmit} className="create-listing-form">
          <TitleField value={formData.title} error={errors.title} onChange={handleChange} />
          <DescriptionField value={formData.description} error={errors.description} onChange={handleChange} />

          {/* Cuisine Type */}
          <div className="form-group">
            <label htmlFor="cuisineType">Cuisine Type</label>
            <select
              id="cuisineType"
              name="cuisineType"
              value={formData.cuisineType}
              onChange={handleChange}
            >
              <option value="">Select a cuisine type</option>
              {CUISINE_TYPES.map(cuisine => (
                <option key={cuisine} value={cuisine}>{cuisine}</option>
              ))}
            </select>
            {errors.cuisineType && <span className="field-error">{errors.cuisineType}</span>}
          </div>

          <MenuField value={formData.menu} error={errors.menu} onChange={handleChange} />

          {/* Date and Time */}
          <div className="form-group">
            <label htmlFor="dateTime">Date & Time (PST)</label>
            <input
              type="datetime-local"
              id="dateTime"
              name="dateTime"
              value={formData.dateTime}
              onChange={handleChange}
            />
            {errors.dateTime && <span className="field-error">{errors.dateTime}</span>}
          </div>

          {/* Booking Cutoff */}
          <div className="form-group">
            <label htmlFor="bookingCutoff">Booking Cutoff</label>
            <select
              id="bookingCutoff"
              name="bookingCutoff"
              value={formData.bookingCutoff}
              onChange={handleChange}
            >
              <option value="">Select booking cutoff time</option>
              {BOOKING_CUTOFF_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {errors.bookingCutoff && <span className="field-error">{errors.bookingCutoff}</span>}
          </div>

          {/* Max Guests */}
          <div className="form-group">
            <label htmlFor="maxGuests">Max Guests</label>
            <input
              type="number"
              id="maxGuests"
              name="maxGuests"
              value={formData.maxGuests}
              onChange={handleChange}
              min={1}
              max={8}
              placeholder="Max 8 guests"
            />
            {errors.maxGuests && <span className="field-error">{errors.maxGuests}</span>}
          </div>

          {/* Neighborhood */}
          <div className="form-group">
            <label htmlFor="neighborhood">Neighborhood</label>
            <select
              id="neighborhood"
              name="neighborhood"
              value={formData.neighborhood}
              onChange={handleChange}
            >
              <option value="">Select a neighborhood</option>
              {NEIGHBORHOODS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {errors.neighborhood && <span className="field-error">{errors.neighborhood}</span>}
          </div>

          {/* Street Address */}
          <div className="form-group">
            <label htmlFor="street">Street Address</label>
            <input
              type="text"
              id="street"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder="Street address (only shown after booking)"
            />
            {errors.street && <span className="field-error">{errors.street}</span>}
          </div>

          {/* Unit Number */}
          <div className="form-group">
            <label htmlFor="unitNumber">Unit Number <span className="optional-label">(Optional)</span></label>
            <input
              type="text"
              id="unitNumber"
              name="unitNumber"
              value={formData.unitNumber}
              onChange={handleChange}
              placeholder="Apt, Suite, Unit, etc."
            />
          </div>

          {/* City */}
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
            />
            {errors.city && <span className="field-error">{errors.city}</span>}
          </div>

          {/* State and Zip Code */}
          <div className="form-row">
            <div className="form-group form-group-half">
              <label htmlFor="state">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="State"
              />
              {errors.state && <span className="field-error">{errors.state}</span>}
            </div>
            <div className="form-group form-group-half">
              <label htmlFor="zipCode">Zip Code</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                placeholder="Zip code"
              />
              {errors.zipCode && <span className="field-error">{errors.zipCode}</span>}
            </div>
          </div>

          <DietaryField selected={formData.dietaryAccommodations} onToggle={handleDietaryChange} />
          <AlcoholField value={formData.alcoholOption} onChange={handleChange} />
          <SpecialNotesField value={formData.specialNotes} onChange={handleChange} />

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Meal'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateListing;
